import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';

// Helper to get first and last day of a given month/year
const getMonthRangeFromYM = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const toISO = d => d.toISOString().slice(0, 10);
  return [toISO(firstDay), toISO(lastDay)];
};

const NewCustomers = () => {
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [startDate, setStartDate] = useState(() => getMonthRangeFromYM(now.getFullYear(), now.getMonth())[0]);
  const [endDate, setEndDate] = useState(() => getMonthRangeFromYM(now.getFullYear(), now.getMonth())[1]);

  const [locations, setLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [showLocationPopup, setShowLocationPopup] = useState(false);
  const locationPopupRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newCustomersCount, setNewCustomersCount] = useState(null);
  const [newCustomersRows, setNewCustomersRows] = useState([]); // <-- existing
  const [topItemsRows, setTopItemsRows] = useState([]); // new: top items by new customers
  const [topItemsLoading, setTopItemsLoading] = useState(false);
  const [topItemsError, setTopItemsError] = useState('');
  const [orderSource, setOrderSource] = useState('All'); // 'All' | 'Online' | 'POS'
  
  const fetchTimeoutRef = useRef();
  
  // helper to format money (no $)
  const formatDollars = (val) => {
    if (val == null || val === '') return '0.00';
    const n = Number(val);
    if (Number.isNaN(n)) return String(val);
    return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  
  useEffect(() => {
    axios.get('/api/reports/locations')
      .then(res => setLocations(res.data || []))
      .catch(() => setLocations([]));
  }, []);

  // Sync selectedMonth when year changes
  useEffect(() => {
    const [, month] = selectedMonth.split('-');
    setSelectedMonth(`${selectedYear}-${month}`);
  }, [selectedYear]);

  // Update start/end date when month changes
  useEffect(() => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const [start, end] = getMonthRangeFromYM(year, month - 1);
    setStartDate(start);
    setEndDate(end);
  }, [selectedMonth]);

  // Close location popup when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (locationPopupRef.current && !locationPopupRef.current.contains(event.target)) {
        setShowLocationPopup(false);
      }
    }
    if (showLocationPopup) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLocationPopup]);

  const changeMonth = (direction) => {
    const [yearStr, monthStr] = startDate.split('-');
    let year = parseInt(yearStr, 10);
    let month = parseInt(monthStr, 10) - 1;
    if (direction === 'prev') {
      month -= 1;
      if (month < 0) { month = 11; year -= 1; }
    } else if (direction === 'next') {
      month += 1;
      if (month > 11) { month = 0; year += 1; }
    }
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const toISO = d => d.toISOString().slice(0, 10);
    setStartDate(toISO(firstDay));
    setEndDate(toISO(lastDay));
    setSelectedMonth(`${year}-${String(month + 1).padStart(2, '0')}`);
    setSelectedYear(year);
  };

  const handleLocationCheckbox = (locId) => {
    setSelectedLocations((prev) => prev.includes(locId) ? prev.filter(id => id !== locId) : [...prev, locId]);
  };

  const fetchData = () => {
    if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
    fetchTimeoutRef.current = setTimeout(() => {
      setLoading(true);
      setTopItemsLoading(true);
      setError('');
      setTopItemsError('');

      const params = {
        startDate,
        endDate,
        selectedLocations: selectedLocations.join(','),
        orderSource, // pass to backend as orderSource
      };

      // fetch new customers and top items in parallel
      Promise.all([
        axios.get('/api/reports/new-customers', { params }),
        axios.get('/api/reports/top-items-new-customers', { params }),
      ])
        .then(([newRes, topRes]) => {
          const payload = newRes.data;

          // handle new-customers response (same logic as before)
          if (Array.isArray(payload)) {
            setNewCustomersRows(payload.map(r => ({
              locationName: r.locationName || r.NAME || r.name || 'Unknown',
              newCustomers: Number(r.newCustomers ?? r.count ?? 0)
            })));
            setNewCustomersCount(null);
          } else if (payload && Array.isArray(payload.newCustomersByLocation)) {
            setNewCustomersRows(payload.newCustomersByLocation.map(r => ({
              locationName: r.locationName || r.NAME || r.name || 'Unknown',
              newCustomers: Number(r.newCustomers ?? r.count ?? 0)
            })));
            setNewCustomersCount(null);
          } else if (payload && typeof payload.newCustomers !== 'undefined') {
            setNewCustomersCount(Number(payload.newCustomers));
            setNewCustomersRows([{
              locationName: selectedLocationNames === 'All Locations' ? 'All Locations' : selectedLocationNames,
              newCustomers: Number(payload.newCustomers)
            }]);
          } else {
            setNewCustomersCount(0);
            setNewCustomersRows([]);
          }

          // handle top-items response
          const topPayload = topRes.data;
          if (Array.isArray(topPayload)) {
            setTopItemsRows(topPayload.map((r, idx) => ({
              rank: idx + 1,
              menuItemId: r.menuItemId ?? r.menu_item_id ?? null,
              itemName: r.itemName ?? r.name ?? r.item_name ?? 'Unknown',
              newCustomerCount: Number(r.newCustomerCount ?? r.newCustomers ?? r.count ?? 0),
              totalQuantity: Number(r.totalQuantity ?? r.totalQuantity ?? r.total_quantity ?? 0),
              totalRevenue: r.totalRevenue ?? r.total_revenue ?? 0,
            })));
          } else {
            setTopItemsRows([]);
          }
        })
        .catch((err) => {
          // prefer to show new-customers error separately if available
          setError('Failed to fetch new customers');
          setTopItemsError('Failed to fetch top items');
          console.error(err);
        })
        .finally(() => {
          setLoading(false);
          setTopItemsLoading(false);
        });
    }, 500);
  };

  useEffect(() => {
    // auto-fetch on relevant changes
    fetchData();
    return () => { if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current); };
    // eslint-disable-next-line
  }, [startDate, endDate, selectedLocations, orderSource]);

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchData();
  };

  const selectedLocationNames = locations
    .filter(loc => selectedLocations.includes(loc.ID))
    .map(loc => loc.NAME || `${loc.CITY} ${loc.NAME}`)
    .join(', ') || 'All Locations';

  // Total new customers summed across returned rows
  const totalNewCustomers = newCustomersRows.reduce((sum, r) => sum + (Number(r.newCustomers) || 0), 0);
  
  return (
    <div style={{ fontFamily: 'Inter, Roboto, Arial, sans-serif' }}>
      <h2>New Customers</h2>
      <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start' }}>
        <div style={{
          minWidth: 260,
          background: '#f7f9fc',
          borderRadius: 12,
          padding: '1rem',
          position: 'relative'
        }}>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <label>
              Month:
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: 6 }}>
                <button type="button" onClick={() => setSelectedYear(y => y - 1)}>&#10913;</button>
                <button type="button" onClick={() => changeMonth('prev')}>&lt;</button>
                <input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} style={{ width: 120 }} />
                <button type="button" onClick={() => changeMonth('next')}>&gt;</button>
                <button type="button" onClick={() => setSelectedYear(y => y + 1)}>&#10914;</button>
              </div>
            </label>

            <label>
              Start Date:
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
            </label>

            <label>
              End Date:
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
            </label>

            <label>
              Locations:
              <div>
                <button type="button" onClick={() => setShowLocationPopup(v => !v)} style={{ marginTop: 6 }}>
                  {selectedLocations.length === 0 ? 'Select Locations' : `${selectedLocations.length} selected`}
                </button>
              </div>
            </label>

            {showLocationPopup && (
              <div ref={locationPopupRef} style={{
                position: 'absolute',
                left: '100%',
                top: 80,
                background: '#fff',
                border: '1px solid #ccc',
                borderRadius: 4,
                padding: 12,
                zIndex: 1000,
                maxHeight: 250,
                width: 270,
                overflowY: 'auto',
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
              }}>
                {locations.length === 0 && <div style={{ color: '#888' }}>No locations</div>}
                {locations.map((loc, idx) => (
                  <div key={`${/*loc.ID || loc.location_id || */loc.NAME}-${idx}`} style={{ marginBottom: 6 }}>
                    <label>
                      <input
                        type="checkbox"
                        checked={selectedLocations.includes(/*loc.ID || loc.location_id || */loc.NAME)}
                        onChange={() => handleLocationCheckbox(/*loc.ID || loc.location_id ||*/ loc.NAME)}
                      />{' '}
                      {loc.NAME} {loc.CITY ? `(${loc.CITY})` : ''}
                    </label>
                  </div>
                ))}
                <div style={{ marginTop: 8 }}>
                  <button type="button" onClick={() => setShowLocationPopup(false)}>Done</button>
                </div>
              </div>
            )}

            <label>
              Order Source:
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                {['All', 'Online', 'POS'].map(src => (
                  <button
                    key={src}
                    type="button"
                    onClick={() => setOrderSource(src)}
                    style={{
                      padding: '6px 10px',
                      borderRadius: 6,
                      border: orderSource === src ? '1px solid #1976d2' : '1px solid #ccc',
                      background: orderSource === src ? '#1976d2' : '#fff',
                      color: orderSource === src ? '#fff' : '#222',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                    title={src}
                  >
                    {src}
                  </button>
                ))}
              </div>
            </label>
  
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="submit">Apply</button>
            </div>
          </form>
        </div>

        <div style={{ flex: 1, background: '#fff', padding: '1rem', borderRadius: 8 }}>
          <div style={{ marginBottom: 12, fontWeight: 600 }}>
            Locations: <span style={{ color: '#8e24aa' }}>{selectedLocationNames}</span>
          </div>

          {loading && <p>Loading...</p>}
          {error && <p style={{ color: 'red' }}>{error}</p>}

          {!loading && !error && (
            <div>
              <h3 style={{ marginTop: 0 }}>New Customers</h3>

              {/* If you still want the big overall number, keep it. */}
              {newCustomersCount !== null && (
                <p style={{ fontSize: '1.5rem', fontWeight: 700, margin: '0.5rem 0' }}>
                  {newCustomersCount}
                </p>
              )}

              {/* Table view showing per-location results (locationName + newCustomers) */}
              {newCustomersRows && newCustomersRows.length > 0 ? (
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #eee' }}>Location</th>
                      <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #eee' }}>New Customers</th>
                    </tr>
                  </thead>
                  <tbody>
                    {newCustomersRows.map((row, idx) => (
                      <tr key={idx}>
                        <td style={{ padding: '8px', borderBottom: '1px solid #fafafa' }}>{row.locationName || 'All Locations'}</td>
                        <td style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #fafafa' }}>{row.newCustomers}</td>
                      </tr>
                    ))}
                    {/* Total row */}
                    <tr style={{ fontWeight: 700, background: '#fafafa' }}>
                      <td style={{ padding: '8px', borderTop: '2px solid #eee' }}>Total</td>
                      <td style={{ padding: '8px', textAlign: 'right', borderTop: '2px solid #eee' }}>{totalNewCustomers}</td>
                    </tr>
                  </tbody>
                </table>
              ) : (
                <p style={{ color: '#666' }}>No new customer data for the selected range.</p>
              )}

              {/* Second table: Top items ordered by new customers */}
              <div style={{ marginTop: 24 }}>
                <h3 style={{ marginTop: 0 }}>Top Items Ordered by New Customers</h3>
                {topItemsLoading && <p>Loading top items...</p>}
                {topItemsError && <p style={{ color: 'red' }}>{topItemsError}</p>}
                {!topItemsLoading && !topItemsError && topItemsRows && topItemsRows.length > 0 ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #eee' }}>#</th>
                        <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #eee' }}>Item</th>
                        <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #eee' }}>Total Qty</th>
                        <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #eee' }}>Total Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topItemsRows.map((row, idx) => (
                        <tr key={idx}>
                          <td style={{ padding: '8px', borderBottom: '1px solid #fafafa' }}>{row.rank}</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #fafafa' }}>{row.itemName}</td>
                          <td style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #fafafa' }}>{row.totalQuantity}</td>
                          <td style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #fafafa' }}>{formatDollars(row.totalRevenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (!topItemsLoading && !topItemsError && topItemsRows.length === 0) ? (
                  <p style={{ color: '#666' }}>No top-item data for the selected range.</p>
                ) : null}
              </div>
  
              <p style={{ color: '#666', marginTop: 10 }}>
                New customers placed their first order within the selected date range.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewCustomers;